import { useState } from 'react';
import { useForm } from 'react-hook-form';
import NewsletterLoading from './NewsletterLoading';
import InputField from '../../components/shared/ContactInputField';
import { ButtonSubmit } from '../../components/ui/Button/ButtonSubmit';

export type NamedFormValues = {
  firstName: string;
  lastName: string;
  email: string;
};

type FormProps = {
  text: {
    firstName: {
      label: string;
      placeholder: string;
      errorMissing: string;
    };
    lastName: {
      label: string;
      placeholder: string;
      errorMissing: string;
    };
    email: {
      label: string;
      placeholder: string;
      errorMissing: string;
      errorInvalid: string;
    };
    loading: string;
    submit: string;
  };
  onSubmit: (data: NamedFormValues) => Promise<void>;
  formMarkup?: React.ReactNode;
  successMarkup?: React.ReactNode;
  id: string;
  infinite?: boolean;
};

const NamedForm = ({
  text,
  onSubmit,
  formMarkup,
  successMarkup,
  id,
  infinite,
}: FormProps) => {
  const [formStatus, setFormStatus] = useState('');
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<NamedFormValues>();

  const onSubmitInside = async (data: NamedFormValues) => {
    setFormStatus('loading');
    try {
      await onSubmit(data);
      if (!infinite) setFormStatus('done');
    } catch {
      setFormStatus('error');
    }
  };

  return (
    <>
      {(formStatus === '' || formStatus === 'error') && (
        <>
          {formMarkup}
          <form onSubmit={handleSubmit(onSubmitInside)}>
            <InputField>
              <label htmlFor={`${id}firstName`}>{text.firstName.label}</label>
              <input
                id={`${id}firstName`}
                type="text"
                placeholder={text.firstName.placeholder}
                {...register('firstName', { required: true })}
              />
              {errors.firstName && <span>{text.firstName.errorMissing}</span>}
            </InputField>
            <InputField>
              <label htmlFor={`${id}lastName`}>{text.lastName.label}</label>
              <input
                id={`${id}lastName`}
                type="text"
                placeholder={text.lastName.placeholder}
                {...register('lastName', { required: true })}
              />
              {errors.lastName && <span>{text.lastName.errorMissing}</span>}
            </InputField>
            <InputField>
              <label htmlFor={`${id}email`}>Email</label>
              <input
                id={`${id}email`}
                type="email"
                placeholder={text.email.placeholder}
                {...register('email', {
                  required: text.email.errorMissing,
                  pattern: {
                    value:
                      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i,
                    message: text.email.errorInvalid,
                  },
                })}
              />
              <span>{errors?.email?.message as string}</span>
            </InputField>

            <ButtonSubmit text={text.submit} className="mt-3" />
          </form>
        </>
      )}
      {formStatus === 'error' && (
        <p role="alert" className="mt-4 rounded-xl border border-current/30 p-4 text-[1.6rem]">
          No pudimos confirmar tu suscripción. Inténtalo de nuevo.
        </p>
      )}
      {formStatus === 'loading' && <NewsletterLoading text={text.loading} />}
      {formStatus === 'done' && successMarkup}
    </>
  );
};

export default NamedForm;
